/**
 * Session Bridge Service - Portage de Flutter session_bridge_web.dart
 * Permet de recevoir le token JWT depuis laventurier-ui (parent) via postMessage
 * pour les appels API éditeur en mode popup/test
 */

interface SupabaseSessionPayload {
  access_token: string | null;
  refresh_token: string | null;
}

interface BridgeMessage {
  type: 'supabase-session' | 'request-session' | 'request-refresh' | 'close-popup';
  payload?: SupabaseSessionPayload;
}

class SessionBridgeService {
  private cachedAccessToken: string | null = null;
  private tokenExpiresAt: Date | null = null;
  private parentOrigin: string | null = null;
  private isInitialized = false;
  private messageListener: ((event: MessageEvent) => void) | null = null;

  /**
   * Initialise le bridge pour recevoir les tokens depuis le parent
   * Portage de Flutter initPopupSessionBridge()
   */
  init(parentOrigin?: string): void {
    if (typeof window === 'undefined') {
      console.log('[SessionBridge] Not on Web platform, skipping bridge initialization');
      return;
    }

    if (this.isInitialized) {
      console.log('[SessionBridge] Bridge already initialized');
      return;
    }

    // Détecter si on est dans une popup
    if (!window.opener) {
      console.log('[SessionBridge] Not in popup context (window.opener is null), skipping bridge');
      return;
    }

    // Déterminer l'origine du parent
    // En production, ça devrait être l'URL de laventurier-ui
    // En dev, par défaut http://localhost:8081 (laventurier-ui)
    this.parentOrigin = parentOrigin || 'http://localhost:8081';
    console.log('[SessionBridge] Initializing bridge with parent origin:', this.parentOrigin);

    // Écouter les messages du parent
    this.messageListener = (event: MessageEvent) => {
      if (event.origin !== this.parentOrigin) {
        console.log(
          '[SessionBridge] Ignoring message from wrong origin:',
          event.origin,
          '(expected:',
          this.parentOrigin,
          ')',
        );
        return;
      }

      const data = event.data as BridgeMessage;
      if (data && typeof data === 'object' && data.type === 'supabase-session') {
        console.log('[SessionBridge] Received supabase-session from parent');
        this.handleSessionMessage(data.payload);
      }
    };

    window.addEventListener('message', this.messageListener);

    // Demander la session initiale au parent
    this.requestSessionFromParent();

    // Aussi demander après des délais au cas où le parent n'est pas encore prêt (session pas encore restaurée)
    setTimeout(() => this.requestSessionFromParent(), 500);
    setTimeout(() => this.requestSessionFromParent(), 1500);
    setTimeout(() => this.requestSessionFromParent(), 3000);

    this.isInitialized = true;
    console.log('[SessionBridge] Bridge initialized successfully');
  }

  /**
   * Gère les messages de session reçus du parent
   */
  private handleSessionMessage(payload: SupabaseSessionPayload | undefined): void {
    if (!payload || !payload.access_token) {
      console.warn('[SessionBridge] No access token in session payload');
      return;
    }

    const accessToken = payload.access_token;
    this.cachedAccessToken = accessToken;

    // Décoder le JWT pour obtenir l'expiration (exp claim)
    try {
      const parts = accessToken.split('.');
      if (parts.length === 3) {
        const payloadPart = parts[1];
        const decoded = JSON.parse(atob(payloadPart.replace(/-/g, '+').replace(/_/g, '/')));
        const exp = decoded.exp as number | undefined;
        if (exp) {
          // exp est un timestamp Unix en secondes
          this.tokenExpiresAt = new Date(exp * 1000);
          const expiresIn = Math.floor((this.tokenExpiresAt.getTime() - Date.now()) / 1000);
          console.log(
            `[SessionBridge] Token expiration decoded from JWT: ${this.tokenExpiresAt.toISOString()} (expires in ${expiresIn}s)`,
          );
        } else {
          // Fallback: estimation 1h si pas de exp
          this.tokenExpiresAt = new Date(Date.now() + 60 * 60 * 1000);
          console.log('[SessionBridge] Token expiration estimated to 1h (no exp in JWT)');
        }
      }
    } catch (e) {
      // En cas d'erreur de décodage, estimation 1h
      this.tokenExpiresAt = new Date(Date.now() + 60 * 60 * 1000);
      console.warn('[SessionBridge] Failed to decode JWT expiration, estimated to 1h:', e);
    }

    console.log('[SessionBridge] Access token cached in memory');
  }

  /**
   * Demande la session au parent via postMessage
   * Portage de Flutter requestSessionFromParent()
   */
  private requestSessionFromParent(): void {
    if (!window.opener || !this.parentOrigin) {
      console.warn('[SessionBridge] Cannot request session: not in popup or parent origin not set');
      return;
    }

    try {
      console.log('[SessionBridge] Requesting session from parent:', this.parentOrigin);
      window.opener.postMessage({type: 'request-session'}, this.parentOrigin);
    } catch (e) {
      console.error('[SessionBridge] Failed to request session from parent:', e);
    }
  }

  /**
   * Attend qu'un token soit disponible (soit déjà en cache, soit arrive bientôt)
   * Portage de Flutter waitForToken()
   */
  async waitForToken(timeoutMs: number = 5000): Promise<string | null> {
    // Si on a déjà un token, le retourner immédiatement
    if (this.cachedAccessToken) {
      return this.cachedAccessToken;
    }

    // Sinon, attendre qu'il arrive
    return new Promise((resolve) => {
      const startTime = Date.now();
      const checkInterval = setInterval(() => {
        if (this.cachedAccessToken) {
          clearInterval(checkInterval);
          resolve(this.cachedAccessToken);
        } else if (Date.now() - startTime > timeoutMs) {
          clearInterval(checkInterval);
          resolve(null);
        }
      }, 100);

      // Demander la session si pas déjà demandée
      if (this.parentOrigin && window.opener) {
        this.requestSessionFromParent();
      }
    });
  }

  /**
   * Récupère l'access token en cache depuis le parent
   * Portage de Flutter getCachedAccessToken()
   */
  getCachedAccessToken(): string | null {
    // Vérifier si le token n'est pas expiré (avec une marge de 60 secondes)
    if (this.cachedAccessToken && this.tokenExpiresAt) {
      const now = new Date();
      const expiresAtWithMargin = new Date(this.tokenExpiresAt.getTime() - 60 * 1000);

      if (now < expiresAtWithMargin) {
        return this.cachedAccessToken;
      } else {
        console.warn(
          `[SessionBridge] Cached token expired or expiring soon (expires at: ${this.tokenExpiresAt.toISOString()}), requesting new one from parent`,
        );
        // Demander un nouveau token au parent
        this.requestSessionFromParent();
        // Retourner le token même s'il est presque expiré (au cas où le parent ne répond pas immédiatement)
        return this.cachedAccessToken;
      }
    }

    return this.cachedAccessToken;
  }

  /**
   * Vérifie si on est dans une popup et si le bridge est disponible
   */
  isInPopup(): boolean {
    return typeof window !== 'undefined' && window.opener !== null;
  }

  /**
   * Nettoie le bridge (retire les listeners)
   */
  cleanup(): void {
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
      this.messageListener = null;
    }
    this.isInitialized = false;
    console.log('[SessionBridge] Bridge cleaned up');
  }
}

export default new SessionBridgeService();
